import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";

export default function SunOrderProductsTable(props: any) {
  const {
    loading,
    values,
    onItemEditComplete,
    onUnitPriceEditComplete,
    askDeleteOrderItem,
    openSunShippingLabelReview,
    showLabelAction = false,
  } = props;

  const quantityEditor = (options: any) => (
    <InputNumber
      value={options.value != null && options.value !== "" ? parseFloat(options.value) : null}
      onValueChange={(e) => options.editorCallback(e.value)}
      onKeyDown={(e) => e.stopPropagation()}
      mode="decimal"
      minFractionDigits={0}
      maxFractionDigits={4}
      min={0}
    />
  );

  const priceEditor = (options: any) => (
    <InputNumber
      value={options.value}
      onValueChange={(e) => options.editorCallback(e.value)}
      onKeyDown={(e) => e.stopPropagation()}
      mode="currency"
      currency="USD"
      locale="en-US"
    />
  );

  const ssccEditor = (options: any) => (
    <InputText
      type="text"
      value={options.value ?? ""}
      onChange={(e) => options.editorCallback(e.target.value)}
      onKeyDown={(e) => e.stopPropagation()}
      className="w-full"
    />
  );

  const onRowEditComplete = async (e: any) => {
    const { newData, index } = e;
    const currentItem = values.products[index];

    const quantityChanged =
      `${currentItem?.quantity ?? ""}` !== `${newData.quantity ?? ""}`;
    const ssccChanged =
      `${currentItem?.sscc_hri_number ?? ""}` !== `${newData.sscc_hri_number ?? ""}`;

    if (quantityChanged || ssccChanged) {
      await onItemEditComplete(newData, index);
    }

    if (`${currentItem?.unit_price ?? ""}` !== `${newData.unit_price ?? ""}`) {
      await onUnitPriceEditComplete(newData, index);
    }
  };

  const productBody = (rowData: any) => (
    <p>
      <span className="text-gray-900 font-semibold">{rowData.product_name}</span>
      <br />
      <span className="text-gray-700">PLU:{rowData.plu ?? `SUN-${rowData.product_id}`}</span>
    </p>
  );

  const orderedQtyBody = (rowData: any) => (
    <p className="text-right">
      {rowData.quantity} {rowData.selling_unit_of_measure}
    </p>
  );

  const unitPriceBody = (rowData: any) => {
    if (rowData.unit_price === null || rowData.unit_price === undefined) {
      return <p className="text-right">$0.00</p>;
    }
    return <p className="text-right">${parseFloat(rowData.unit_price).toFixed(2)}</p>;
  };

  const totalPriceBody = (rowData: any) => {
    const total = rowData.total_dollar_amount ?? 0;
    return <p className="text-right">${parseFloat(total || 0).toFixed(2)}</p>;
  };

  const ssccBody = (rowData: any) => (
    <p>{rowData.sscc_hri_number ? rowData.sscc_hri_number : "—"}</p>
  );

  const actionsBody = (rowData: any) => (
    <div className="flex justify-end items-center gap-2">
      {showLabelAction ? (
        <button
          type="button"
          onClick={() => openSunShippingLabelReview?.(rowData)}
          className="rounded-md bg-teal-600 px-1.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
        >
          Label
        </button>
      ) : null}
      <button
        onClick={() => askDeleteOrderItem(rowData)}
        className="rounded-md bg-red-600 px-1.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
      >
        Delete
      </button>
    </div>
  );

  const totalCases = (values.products ?? []).reduce(
    (sum: number, item: any) => sum + parseFloat(item.quantity ?? 0),
    0
  );

  return (
    <div className="mt-5">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          {loading ? (
            <div className="border h-10 shadow rounded-md p-4 w-full mx-auto">
              <div className="animate-pulse flex space-x-4">Loading</div>
            </div>
          ) : (
            <div className="overflow-hidden">
              <DataTable
                value={values.products}
                dataKey="id"
                editMode="row"
                onRowEditComplete={onRowEditComplete}
                tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm sun-order-products-table-parent"
                cellClassName={() => "p-2 bg-white border-1-bottom-gray text-sm"}
              >
                <Column header="Product" body={productBody} />
                <Column
                  field="sscc_hri_number"
                  header="SSCC HRI #"
                  body={ssccBody}
                  editor={(options) => ssccEditor(options)}
                />
                <Column
                  field="quantity"
                  header="Ordered Qty"
                  body={orderedQtyBody}
                  editor={(options) => quantityEditor(options)}
                  headerClassName="text-right"
                  bodyClassName="text-right"
                  style={{ textAlign: "right" }}
                />
                <Column
                  field="unit_price"
                  header="Unit price"
                  body={unitPriceBody}
                  editor={(options) => priceEditor(options)}
                  headerClassName="text-right"
                  bodyClassName="text-right"
                  style={{ textAlign: "right" }}
                />
                <Column
                  header="Total price"
                  body={totalPriceBody}
                  headerClassName="text-right"
                  bodyClassName="text-right"
                  style={{ textAlign: "right" }}
                />
                <Column
                  rowEditor={true}
                  headerStyle={{ width: "10%", minWidth: "8rem" }}
                  bodyStyle={{ textAlign: "center" }}
                />
                <Column header="" body={actionsBody} style={{ textAlign: "right" }} />
              </DataTable>

              <div className="mt-6 flex justify-end">
                <div className="w-full max-w-sm space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-md font-medium text-gray-600">Subtotal:</span>
                    <span className="text-md font-semibold text-gray-900">
                      ${parseFloat(`${values.subtotal_dollar_amount || 0}`).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-md font-medium text-gray-600">Tax:</span>
                    <span className="text-md font-semibold text-gray-900">$0.00</span>
                  </div>
                  <div className="border-t border-gray-300 pt-2 mt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-900">Total:</span>
                      <span className="text-lg font-bold text-gray-900">
                        ${parseFloat(`${values.total_dollar_amount || 0}`).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900">Total Cases:</span>
                    <span className="text-lg font-bold text-gray-900">{totalCases}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
